import { useState } from "react";
import type { ConsentBasis } from "@mmm/contracts";
import { Button, Card, useT } from "@mmm/ui";
import { acceptConsent } from "../../api/commands.js";
import { useAuth } from "../../auth/AuthProvider.js";
import { BrandScreen } from "../../components/shell/BrandScreen.js";
import { PRIVACY_PATH, TERMS_PATH, publicSiteOrigin } from "../../publicSite.js";
import { useRobotsMeta } from "../../seo/useRobotsMeta.js";

/**
 * Asked once, before anyone reaches their workspace: are you 18 or over, or has
 * a parent or guardian agreed for you?
 *
 * Many learners are under 18, and POPIA allows a child's personal information to
 * be processed only with a parent's or guardian's consent. So this records which
 * of the two applies, when, and which version of the documents was shown. Each
 * answer is its own button, so agreeing is a deliberate act rather than a box
 * left ticked.
 *
 * `version` is the server's current version. It is sent back with the answer so
 * the server can refuse if the documents changed while this screen was open.
 * There is no navigation after success: recording consent refreshes the session,
 * and RequireConsent stops rendering this screen.
 */
export function ConsentScreen({ version }: { version: string }) {
  useRobotsMeta("noindex, nofollow");
  const t = useT();
  const { signOut } = useAuth();
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const origin = publicSiteOrigin();

  const choose = async (basis: ConsentBasis) => {
    setSaving(true);
    setFailed(false);
    try {
      await acceptConsent({ basis, version });
    } catch {
      setFailed(true);
      setSaving(false);
    }
  };

  return (
    <BrandScreen className="mmm-auth-screen mmm-consent-screen" role="main" aria-label={t("consent.title")}>
      <Card padding="lg" className="mmm-consent-card">
        <h1>{t("consent.title")}</h1>
        <p>{t("consent.intro")}</p>

        <p className="mmm-consent-docs">
          <a href={`${origin}${TERMS_PATH}`} target="_blank" rel="noreferrer">
            {t("footer.terms")}
          </a>
          <a href={`${origin}${PRIVACY_PATH}`} target="_blank" rel="noreferrer">
            {t("footer.privacy")}
          </a>
        </p>

        <div className="mmm-consent-choice">
          <Button variant="primary" disabled={saving} onClick={() => void choose("adult")}>
            {t("consent.adultAction")}
          </Button>
          <p>{t("consent.adultHint")}</p>
        </div>

        <div className="mmm-consent-choice">
          <Button variant="secondary" disabled={saving} onClick={() => void choose("guardian")}>
            {t("consent.guardianAction")}
          </Button>
          <p>{t("consent.guardianHint")}</p>
        </div>

        {failed && (
          <p role="alert" className="mmm-consent-error">
            {t("consent.saveFailed")}
          </p>
        )}

        <p className="mmm-consent-signout">
          {t("consent.noAgree")}
          <Button variant="text" disabled={saving} onClick={() => void signOut()}>
            {t("settings.signOut")}
          </Button>
        </p>
      </Card>
    </BrandScreen>
  );
}
